import { Link } from "react-router-dom";
import { ClipboardCheck, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/ui-bits";
import { ARTICLES } from "@/data/articles";
import { SERVICES, SERVICE_ORDER } from "@/data/services";
import AskAndrogen from "@/components/AskAndrogen";

const GROUPS = [
  { id: "symptoms-testing", name: "Symptoms, testing & diagnosis", labels: ["SYMPTOMS & DIAGNOSIS", "SYMPTOM CHECK", "TESTING EXPLAINED"] },
  { id: "causes-lifestyle", name: "Causes & lifestyle", labels: ["CAUSES & RELATED HEALTH", "LIFESTYLE"] },
  { id: "treatment", name: "Treatment & expectations", labels: ["TREATMENT COMPARISON", "TREATMENT EXPECTATIONS"] },
  { id: "safety-monitoring", name: "Safety, monitoring & fertility", labels: ["SAFETY", "MONITORING", "FERTILITY"] },
  { id: "navigating-care", name: "Navigating care", labels: ["NAVIGATING CARE"] },
];

export function ArticleCard({ a, delay = 0, testIdPrefix = "learn-card" }) {
  return (
    <Reveal delay={delay} className="h-full">
      <Link
        to={`/learn/${a.slug}`}
        className="card-line rounded-2xl overflow-hidden flex flex-col h-full group hover:border-teal/30 transition-colors"
        data-testid={`${testIdPrefix}-${a.slug}`}
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={a.image}
            alt={a.imageAlt || ""}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-[1.03] transition-all duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/30 to-transparent" />
          <p className="absolute left-5 bottom-4 mono-label text-teal">{a.label}</p>
        </div>
        <div className="p-6 flex flex-col flex-1">
          <h3 className="font-display text-2xl tracking-wide leading-tight group-hover:text-teal transition-colors">{a.title}</h3>
          <p className="text-mute text-sm leading-relaxed mt-3 flex-1">{a.lede.slice(0, 110)}…</p>
          <p className="mono-label text-dusk mt-5">{a.reading}</p>
        </div>
      </Link>
    </Reveal>
  );
}

function AdamBanner() {
  const adam = ARTICLES.find((a) => a.slug === "adam-questionnaire");
  return (
    <Reveal>
      <Link
        to="/learn/adam-questionnaire"
        className="card-line rounded-3xl overflow-hidden grid lg:grid-cols-12 group hover:border-teal/30 transition-colors"
        data-testid="learn-adam-banner"
      >
        <div className="lg:col-span-5 relative min-h-[220px]">
          <img src={adam.image} alt={adam.imageAlt} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-surface hidden lg:block" />
          <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent lg:hidden" />
        </div>
        <div className="lg:col-span-7 p-8 sm:p-10 flex flex-col justify-center">
          <Label>
            <ClipboardCheck size={14} /> Interactive · 2 minutes
          </Label>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight mt-4">
            Take the ADAM questionnaire —
            <span className="serif-it text-teal"> then understand what it means.</span>
          </h2>
          <p className="text-mute text-sm sm:text-base leading-relaxed mt-4 max-w-xl">
            Ten yes/no questions used by clinicians to decide whether a testosterone blood test is
            worthwhile. Scored instantly, explained honestly, never a diagnosis.
          </p>
          <span className="inline-flex items-center gap-2 text-teal text-sm mt-6 group-hover:gap-3 transition-all">
            Start the questionnaire <ArrowRight size={16} />
          </span>
        </div>
      </Link>
    </Reveal>
  );
}

export default function LearnHub() {
  return (
    <>
      <Seo
        title="Testosterone Knowledge Centre | Androgen Health"
        description="Evidence-based guides on low testosterone symptoms, normal levels, causes, TRT risks, fertility, monitoring and lifestyle — plus the ADAM questionnaire. Written for patients, reviewed for launch."
      />
      <PageHero
        label="Knowledge centre"
        title={
          <>
            The UK's most useful
            <br />
            <span className="serif-it text-teal">testosterone resource.</span>
          </>
        }
        lede={`${ARTICLES.length} clinical guides. Every one leads with the direct answer, explains the clinical context, and is honest about uncertainty. No keyword stuffing, no invented statistics — just careful writing, due clinical review.`}
        safety={false}
      />

      <section className="pb-20 px-5 sm:px-8" data-testid="learn-grid-section">
        <div className="max-w-7xl mx-auto">
          <AdamBanner />

          <nav className="flex flex-wrap gap-2 mt-14" aria-label="Guide categories" data-testid="learn-category-nav">
            {GROUPS.map((g) => (
              <a
                key={g.id}
                href={`#${g.id}`}
                className="px-4 py-2 rounded-full border border-line mono-label text-dusk hover:text-teal hover:border-teal/40 transition-colors"
                data-testid={`learn-category-link-${g.id}`}
              >
                {g.name}
              </a>
            ))}
          </nav>

          {GROUPS.map((g) => {
            const items = ARTICLES.filter((a) => g.labels.includes(a.label));
            return (
              <div key={g.id} id={g.id} className="mt-16 scroll-mt-28" data-testid={`learn-category-${g.id}`}>
                <Reveal>
                  <div className="flex items-end justify-between gap-4">
                    <Label>{g.name}</Label>
                    <p className="mono-label text-dusk">{items.length} {items.length === 1 ? "guide" : "guides"}</p>
                  </div>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
                  {items.map((a, i) => (
                    <ArticleCard key={a.slug} a={a} delay={i * 0.06} />
                  ))}
                </div>
              </div>
            );
          })}

          <Reveal>
            <div className="mt-20">
              <Label>Service pages</Label>
              <div className="flex flex-wrap gap-3 mt-6">
                {SERVICE_ORDER.map((slug) => (
                  <Link key={slug} to={`/${slug}`} className="px-5 py-2.5 rounded-full border border-line text-sm text-mute hover:text-teal hover:border-teal/40 transition-colors" data-testid={`learn-service-link-${slug}`}>
                    {SERVICES[slug].title}
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="mt-20 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <Reveal>
                <Label>AI health library — beta</Label>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight mt-5">
                  Ask Androgen,
                  <br />
                  <span className="serif-it text-teal">any hour.</span>
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="text-mute text-base leading-relaxed mt-5">
                  Educational answers on symptoms, testing, treatment and safety — powered by Claude or
                  GPT, with clinical guardrails and no diagnosis.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <AskAndrogen />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
